// What a manager submits before a draft. BACKLOG D2, DRAFT_MODES §4.
//
// "It is the whole of §3.2's input, and it is what a person sits down with a
// coffee and fills in." Nothing in this project has stored a manager's INTENT
// before — `drafts` and `draft_picks` record what happened — so this is the
// input the resolver (D3) resolves and the autopick runner (D5) runs on.
//
// TWO ORDERINGS, ONE RESOLVER, and `ordering` says which is real. Ken has used
// both: one overall list with best-available (ESPN, Yahoo) and a list per
// position with a priority order (NFL.com's positions-by-round). They are
// genuinely different ways to think and they must not be two engines, so both
// reduce at every pick to the same question — which positions may I take now,
// and who is the best remaining one of those on this manager's list.
//
// THEY DO NOT COEXIST. If an overall list and per-position lists both existed
// and disagreed, somebody would have to define which wins, and no platform has
// a good answer. `ordering` picks one — and THE LEAGUE CHOOSES WHICH, not the
// manager: it is `LeagueSettings.draft.ranking`, set by the commissioner when
// the league is made (since 2026-10-08; the 2026-09-17 record had it the
// manager's). A plan stored before the commissioner changed it is converted
// when it is read, and the view says so; one saved in the wrong style is
// refused.

import { z } from 'zod';
import { DraftRanking } from '../rules/schemas';

/**
 * Per-position minimum and maximum, per manager.
 *
 * THREE RULES LIVE ON THE RESOLVER, NOT HERE, and D3 owns them — but the third
 * is why this shape exists at all, so it is written down where somebody adding
 * a field will read it:
 *
 *   1. `max` is a veto, never a nudge. The resolver skips a player whose
 *      position is capped; it never reaches for somebody to satisfy anything.
 *   2. `min` is satisfied as late as it still can be — a minimum reserves the
 *      LAST picks that could fill it, never an early one.
 *   3. A limit must never become a reason to draft somebody early. This is the
 *      number one complaint about unattended drafting everywhere, and Yahoo is
 *      the cautionary case: filling starters before bench produces a kicker in
 *      round 9 and a defense in round 6, and the standard player workaround is
 *      to exclude every kicker so the balancer has nothing to reach for.
 *      **When a manager's list and a minimum disagree and there are still
 *      enough rounds to fill the minimum later, the manager's list wins.**
 */
export const PositionLimit = z
  .object({
    min: z.number().int().min(0).max(99),
    max: z.number().int().min(0).max(99),
  })
  .refine((l) => l.max >= l.min, { message: 'max must be at least min' });
export type PositionLimit = z.infer<typeof PositionLimit>;

/** A position, or "whoever is best on my list" — one entry per round. */
export const RoundIntent = z.string().min(1);

export const DraftPlan = z.object({
  teamId: z.string().min(1),
  /** Which list is real for this plan: always the league's `draft.ranking` once read. */
  ordering: DraftRanking,
  /**
   * `ordering: 'overall'` — one list, best to worst, positions mixed.
   *
   * Capped because a plan is one stored blob and a list nobody could have
   * built by hand is a sign of a client sending something else. A real draft
   * pool is a few hundred players; 1,000 is room and then some.
   */
  overall: z.array(z.string().min(1)).max(1000).default([]),
  /** `ordering: 'by-position'` — one list per position. */
  byPosition: z.record(z.string(), z.array(z.string().min(1)).max(1000)).default({}),
  /**
   * Always present, always defaulted from the league's roster before anybody
   * opens the screen — one QB starter and six bench gives `QB {min: 1, max: 2}`,
   * not `{min: 0, max: 99}`. The protection is on before anyone touches
   * anything, which is the right posture and the one RTSports takes.
   */
  limits: z.record(z.string(), PositionLimit).default({}),
  /**
   * The pick order: a position per round. By position only, and there every
   * round names one, so it is also how many of each the draft takes; one list
   * has none (`planProblem`). `best` is still read by the resolver — it was a
   * by-position choice until 2026-10-08 — and no plan may be saved with it.
   */
  roundPlan: z.array(RoundIntent).max(100).default([]),
  /**
   * Never draft these, at any price.
   *
   * Built on day one rather than later, because without it the only way to say
   * *never this guy* is to rank three hundred players — and Yahoo users are
   * already abusing its exclusion list as a substitute for the position caps
   * Yahoo lacks. Give them both and neither gets misused.
   */
  doNotDraft: z.array(z.string().min(1)).max(1000).default([]),
  /**
   * In an auction league, the most this manager would pay for each player they
   * priced (AUCTION.md §7): what their seat bids up to when they are away or
   * the draft runs by itself. Private to them. Absent in a pick draft's plan,
   * and in every plan stored before 2026-10-15.
   */
  prices: z.record(z.string().min(1), z.number().int().min(0).max(100_000)).optional(),
  /** Null until the manager says they are done. Set by the server, not the client. */
  submittedAt: z.string().nullable().default(null),
});
export type DraftPlan = z.infer<typeof DraftPlan>;

/**
 * What a client may send.
 *
 * `teamId` and `submittedAt` are omitted deliberately: the team comes from the
 * path and the session, and the timestamp is the server's to set. A client that
 * could send either could claim somebody else's plan, or claim it was submitted
 * before a lock it missed.
 */
export const SaveDraftPlanRequest = DraftPlan.omit({ teamId: true, submittedAt: true }).extend({
  /** True when the manager is saying they are done, not just saving progress. */
  submit: z.boolean().default(false),
});
export type SaveDraftPlanRequest = z.infer<typeof SaveDraftPlanRequest>;

/** What a plan is checked against: the league's, never the manager's. */
export interface PlanRules {
  positions: readonly string[];
  rounds: number;
  /** The league's `draft.ranking`. A plan in the other style is refused. */
  ranking: DraftRanking;
  /**
   * By position: the fewest times the pick order may name each position, which
   * is how many the lineup starts. A FLEX raises nobody's (`slotDemand`).
   */
  minimumPicks: Readonly<Record<string, number>>;
  /** The commissioner's own most at each position (`roster.maxPerPosition`). */
  leagueCaps: Readonly<Record<string, number>>;
}

/**
 * The refusal for a plan saved in the style the league does not use — a tab
 * left open across the commissioner's change, most likely. Its own function
 * because the stores refuse it as a conflict, before `planProblem`.
 */
export function rankingMismatch(ranking: DraftRanking): string {
  return ranking === 'overall'
    ? 'This league ranks in one list now. Reload to see your plan as one list.'
    : 'This league ranks by position now. Reload to see your plan by position.';
}

/**
 * Why a plan cannot be drafted from, or null.
 *
 * THE ENDPOINT'S SCHEMA IS THE ENGINE'S PRECONDITION, WRITTEN DOWN. The shape
 * above checks that a limit is two small integers and a round plan is a list
 * of strings; it cannot know which positions this league has or how many
 * rounds its draft is, and the first version stopped there. So `limits.ZZ`,
 * a plan whose maxes summed below the round count, and a round plan naming
 * `banana` were all stored with 200 — and each one left the resolver with
 * nobody to take and the whole league's draft stuck (AUDIT-2026-09-23 RD1).
 * The resolver no longer stalls on any of them, but a plan that cannot be
 * honored is still wrong at the door, where the person who typed it is
 * looking, rather than on draft night, where nobody is.
 *
 * THE TWO STYLES DO NOT SHARE PARTS (2026-10-08). One list has limits and no
 * pick order; by position has a position for every round and no limits,
 * because the pick order already says how many of each — a limit beside it
 * could only disagree with it. The screen calls this too, so the person sees
 * the sentence before pressing Save rather than after.
 *
 * Pure and sport-free: the positions and the round count are handed in.
 */
export function planProblem(input: SaveDraftPlanRequest, rules: PlanRules): string | null {
  if (input.ordering !== rules.ranking) return rankingMismatch(rules.ranking);
  const known = new Set(rules.positions);
  for (const key of [...Object.keys(input.limits), ...Object.keys(input.byPosition)]) {
    if (!known.has(key)) return `That plan names a position this league cannot hold: ${key}.`;
  }
  for (const intent of input.roundPlan) {
    if (intent !== 'best' && !known.has(intent)) {
      return `That plan names a position this league cannot hold: ${intent}.`;
    }
  }
  return input.ordering === 'by-position'
    ? pickOrderProblem(input, rules)
    : limitsProblem(input, rules.positions, rules.rounds);
}

/** By position: every round names a position, and the counts fit the lineup and the caps. */
function pickOrderProblem(input: SaveDraftPlanRequest, rules: PlanRules): string | null {
  if (input.overall.length > 0) return 'A plan by position has no single list.';
  if (Object.keys(input.limits).length > 0) {
    return 'A plan by position has no position limits: its pick order says how many of each.';
  }
  if (input.roundPlan.length !== rules.rounds) {
    return `Your pick order names ${input.roundPlan.length} round(s), and the draft is ${rules.rounds}.`;
  }
  const missing = input.roundPlan.findIndex((p) => p === 'best');
  if (missing !== -1) return `Round ${missing + 1} of your pick order needs a position.`;

  const named: Record<string, number> = {};
  for (const p of input.roundPlan) named[p] = (named[p] ?? 0) + 1;
  // A league whose lineup starts more than its draft has rounds cannot be
  // held to its lineup by anybody's pick order, so it is not held to it.
  const starts = Object.values(rules.minimumPicks).reduce((n, m) => n + m, 0);
  for (const position of rules.positions) {
    const need = rules.minimumPicks[position] ?? 0;
    const have = named[position] ?? 0;
    if (starts <= rules.rounds && have < need) {
      return `Your pick order names ${have} ${position}, and the lineup starts ${need}.`;
    }
    const cap = rules.leagueCaps[position];
    if (cap !== undefined && have > cap) {
      return `Your pick order names ${have} ${position}, and this league allows at most ${cap}.`;
    }
  }
  return null;
}

/** One list: no pick order, and limits the draft can meet. */
function limitsProblem(
  input: SaveDraftPlanRequest,
  positions: readonly string[],
  rounds: number,
): string | null {
  if (Object.keys(input.byPosition).length > 0) return 'A one-list plan has no lists by position.';
  if (input.roundPlan.length > 0) return 'A one-list plan has no pick order.';
  const limits = Object.values(input.limits);
  // Only a plan that caps EVERY position can cap the whole draft; a position
  // left off the list is uncapped, and the draft can always fill from it.
  if (limits.length > 0 && positions.every((p) => input.limits[p] !== undefined)) {
    const allowed = limits.reduce((n, l) => n + l.max, 0);
    if (allowed < rounds) {
      return `Your limits allow at most ${allowed} player(s), and the draft is ${rounds} rounds.`;
    }
  }
  const required = limits.reduce((n, l) => n + l.min, 0);
  if (required > rounds) {
    return `Your limits require at least ${required} player(s), and the draft is only ${rounds} rounds.`;
  }
  return null;
}

/**
 * The plan as a screen reads it, with the two things a screen cannot work out.
 *
 * `lockedAt` and `locked` are served rather than computed by the client for the
 * reason the lineup lock is (ICD-S01 §4): a client that decides for itself when
 * planning closed will disagree with the server across a timezone, a stale tab
 * or a wrong clock, and the disagreement surfaces as a save that is silently
 * refused.
 */
export const DraftPlanView = z.object({
  plan: DraftPlan,
  /** When planning closes. Null when the league has not set a draft time. */
  lockedAt: z.string().nullable(),
  /** Whether it is closed NOW, by the server's clock. */
  locked: z.boolean(),
  /**
   * The positions the league's roster can start, in the sport's order, so a
   * screen can draw the limits it must fill. Every position the sport has
   * until 2026-10-18 (Q11).
   */
  positions: z.array(z.string()),
  /**
   * How many rounds the draft has — how long a `roundPlan` is. A screen that
   * lays out a pick per round cannot know it otherwise (2026-09-24, the
   * by-position priorities).
   */
  rounds: z.number().int().min(1),
  /**
   * The style the plan was SAVED in, when the league has since changed to the
   * other one; null otherwise. The plan above is already converted — one list
   * split by position with a pick order filled in, or the lists merged with
   * the house ranking deciding between positions — and stays that way only
   * once it is saved, so a screen says so and treats it as unsaved.
   */
  convertedFrom: DraftRanking.nullable(),
  /**
   * The fewest times a pick order by position may name each position — what
   * the lineup starts. Served whatever the style, and read only by position.
   */
  minimumPicks: z.record(z.string(), z.number().int().min(0)),
  /** The commissioner's own most at each position, which no plan may pass. */
  leagueCaps: z.record(z.string(), z.number().int().min(0)),
  updatedAt: z.string(),
});
export type DraftPlanView = z.infer<typeof DraftPlanView>;

/** How many of each position a roster demands, split by the two kinds of slot. */
export interface SlotDemand {
  /** Dedicated starter slots: this position must be started this many times. */
  starters: Record<string, number>;
  /** Flex slots that accept this position. Raises a ceiling, never a minimum. */
  flexSlotsFor: Record<string, number>;
}

/**
 * What a roster's slots ask of each position.
 *
 * Pulled out of `defaultLimits` because D5's draft board needs the same split
 * for a different question — where replacement level sits at each position —
 * and two loops over the same slots would be two chances to disagree about
 * what a flex means.
 */
export function slotDemand(
  slots: readonly { slot: string; count: number }[],
  eligibility: Readonly<Record<string, readonly string[]>>,
): SlotDemand {
  const starters: Record<string, number> = {};
  const flexSlotsFor: Record<string, number> = {};
  for (const { slot, count } of slots) {
    const accepts = eligibility[slot] ?? [];
    if (accepts.length === 1) {
      // A dedicated slot: this position must be started this many times.
      starters[accepts[0]!] = (starters[accepts[0]!] ?? 0) + count;
    } else if (accepts.length > 1) {
      // A FLEX raises nobody's minimum — it can be filled by any of several
      // positions, so it requires none of them. It does raise their ceilings.
      for (const p of accepts) flexSlotsFor[p] = (flexSlotsFor[p] ?? 0) + count;
    }
  }
  return { starters, flexSlotsFor };
}

/**
 * The limits a league's roster implies, before a manager changes anything.
 *
 * `min` is what you must START — you need at least as many as the lineup
 * demands. `max` is that plus headroom, and **how much headroom depends on
 * whether the position can fill a flex**, which is the distinction the first
 * version of this missed. DRAFT_MODES §4.2's worked example is the check that
 * found it: one QB starter and six bench must give `QB { min: 1, max: 2 }`, and
 * a formula that scaled headroom with the bench gave 3.
 *
 *   - A position that CANNOT fill a flex gets exactly one spare. Nobody carries
 *     two backup kickers, and a default that allows it invites a plan to spend
 *     a pick proving it.
 *   - A position that CAN gets the flex slots that accept it plus a share of
 *     the bench, because those are the positions a manager actually stacks.
 *
 * A cap is not a budget: the totals may exceed the roster, and should. It is a
 * veto on going deep at one position, not an allocation of the whole squad.
 *
 * Pure, and it names no sport: slots and their eligibility are handed in.
 */
export function defaultLimits(
  slots: readonly { slot: string; count: number }[],
  eligibility: Readonly<Record<string, readonly string[]>>,
  benchCount: number,
): Record<string, PositionLimit> {
  const { starters, flexSlotsFor } = slotDemand(slots, eligibility);

  const flexible = Object.keys(flexSlotsFor).length;
  const limits: Record<string, PositionLimit> = {};
  for (const position of new Set(Object.values(eligibility).flat())) {
    const min = starters[position] ?? 0;
    const flexSlots = flexSlotsFor[position] ?? 0;
    const headroom = flexSlots > 0 ? flexSlots + Math.ceil(benchCount / Math.max(1, flexible)) : 1;
    limits[position] = { min, max: min + headroom };
  }
  return limits;
}

/**
 * One player a plan may name, as the priorities screen lists them.
 * PLAN-2026-09-24 item B.
 */
export const DraftPoolPlayer = z.object({
  playerId: z.string(),
  name: z.string(),
  positions: z.array(z.string()),
  proTeam: z.string().nullable(),
  /**
   * 1-based, in the order the draft's HOUSE LIST would take them — value over
   * replacement in this league's scoring and roster, which is what picks for a
   * manager once their own list runs out. Not points per game: ordered by that,
   * the top of the list is every quarterback in the league, and a draft run on
   * it takes six in round one.
   */
  rank: z.number().int().min(1),
  /** Mean points per game in the basis season, in this league's scoring. Null for no games. */
  perGame: z.number().nullable(),
  /** `perGame` above the last starter the league will field at this position. Null for no games. */
  overReplacement: z.number().nullable(),
  /**
   * In an auction league, what the house thinks he is worth in the league's
   * money (AUCTION.md §7): what a price starts at. Null in a pick draft.
   */
  value: z.number().int().nullable().default(null),
});
export type DraftPoolPlayer = z.infer<typeof DraftPoolPlayer>;

/** `GET /api/leagues/:id/draft/pool` — every player a plan may name, best first. */
export const DraftPoolView = z.object({
  /** What the numbers were built from, in words, for the screen. */
  basis: z.string(),
  /**
   * The league's auction: every team's budget, the lowest bid, and the spots
   * it fills (AUCTION.md §7). Null for a pick draft, where a plan has no prices.
   */
  auction: z
    .object({ budget: z.number().int(), minBid: z.number().int(), spots: z.number().int() })
    .nullable()
    .default(null),
  players: z.array(DraftPoolPlayer),
  updatedAt: z.string(),
});
export type DraftPoolView = z.infer<typeof DraftPoolView>;

/**
 * `POST /api/leagues/:id/draft/manual` — a draft held in person, entered by
 * the commissioner afterward (BACKLOG D7, scenario C5). One step a request:
 *
 * - `start`: the order the league drafted in, every team once, round one
 *   first. Again until the first pick is in; after that the order stands.
 * - `pick`: the player taken at pick `overall`, which has to be the next one.
 *   The team is the turn's: worked out from the order and the round, never
 *   sent. Naming the pick means a screen a pick behind is refused rather than
 *   landing one place later than it meant.
 * - `sale`: an auction's (AUCTION.md §6.3): the player, the team that bought
 *   him and the price. Checked against the team's most, so a typo of $85 for
 *   $8 is refused where it is made. An auction has no `pick`.
 * - `undo`: take back pick `overall`, which has to be the last one in — in an
 *   auction, the last sale entered.
 * - `finish`: every pick is in; the rosters, the first lineups and the
 *   announcement follow, as for an autopick draft.
 *
 * Answers with the draft as `GET /api/leagues/:id/draft` serves it, so the
 * screen re-renders from what it gets back.
 */
export const ManualDraftRequest = z.discriminatedUnion('action', [
  z.object({ action: z.literal('start'), order: z.array(z.string().min(1)).min(2).max(20) }),
  z.object({
    action: z.literal('pick'),
    overall: z.number().int().min(1),
    playerId: z.string().min(1),
  }),
  z.object({
    action: z.literal('sale'),
    teamId: z.string().min(1),
    playerId: z.string().min(1),
    price: z.number().int().min(0).max(100_000),
  }),
  z.object({ action: z.literal('undo'), overall: z.number().int().min(1) }),
  z.object({ action: z.literal('finish') }),
]);
export type ManualDraftRequest = z.infer<typeof ManualDraftRequest>;
